import React, { useEffect, useRef, useState } from 'react';
import { getAttribution } from '../lib/attribution';
import { trackEvent, trackOnce, EVENTS } from '../lib/analytics';

/*
  Public lead form for landing pages.

  Posts SAME-ORIGIN to the PHP proxy /lead.php on eagentix.com, which forwards
  to the FastAPI backend (over the Cloudflare tunnel). Same-origin = no browser
  CORS, and the backend secret stays in PHP, never in this bundle.

  Setup (.env):
    REACT_APP_TURNSTILE_SITE_KEY=0x4AAAAA...
    REACT_APP_LEADS_ENDPOINT=/lead.php        (optional; default below)
*/

const ENDPOINT = process.env.REACT_APP_LEADS_ENDPOINT || '/lead.php';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

function useTurnstile(widgetRef, onToken) {
  useEffect(() => {
    if (!SITE_KEY) return;
    let id;
    const render = () => {
      if (window.turnstile && widgetRef.current && !widgetRef.current.dataset.rendered) {
        widgetRef.current.dataset.rendered = '1';
        window.turnstile.render(widgetRef.current, {
          sitekey: SITE_KEY,
          callback: onToken,
          'error-callback': () => onToken(''),
          'expired-callback': () => onToken(''),
        });
      }
    };
    if (window.turnstile) {
      render();
    } else {
      if (!document.querySelector('script[src*="turnstile"]')) {
        const s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        s.async = true;
        s.defer = true;
        document.body.appendChild(s);
      }
      id = setInterval(render, 300);
    }
    return () => id && clearInterval(id);
  }, [widgetRef, onToken]);
}

export default function LeadForm({
  page = '',
  accent = '#0071e3',
  showTeamSize = false,
  title = 'Get in Touch',
  subtitle = 'Tell us what you need. We reply within 1 business day.',
  buttonLabel = 'Send Message',
  messageLabel = 'How can we help? (optional)',
  messagePlaceholder = 'Tell us a bit about your project or question',
  successText = 'Our team will reach out within 1 business day.',
  showPhone = true,
  showJobTitle = false,
  jobTitleLabel = 'Job title',
  teamSizeLabel = 'Finance team size',
  teamSizeOptions = ['1-5', '6-15', '16-40', '40+'],
  consentLabel = 'Send me occasional Eagentix insights and service updates.',
  successActionUrl = '',
  successActionLabel = 'Choose a time',
}) {
  const fieldPrefix = (page || 'general').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
  const [form, setForm] = useState({
    name: '', work_email: '', company: '', phone: '', job_title: '', team_size: '',
    message: '', marketing_consent: false, website: '',
  });
  const [token, setToken] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');
  const widgetRef = useRef(null);
  useTurnstile(widgetRef, setToken);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Paired with generate_lead this gives form abandonment rate — the single
  // most actionable number on a lead-gen page. Fires once per form, per session.
  const onFirstInteraction = () => {
    trackOnce(EVENTS.FORM_START, { page: page || window.location.pathname, form_title: title }, page || title);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (SITE_KEY && !token) { setError('Please complete the verification.'); return; }
    setState('sending');
    try {
      // page = explicit prop (training landers) → tracked origin page
      // (content page the visitor came from) → current path.
      let originPage = '';
      try { originPage = sessionStorage.getItem('lead_origin') || ''; } catch (_) {}
      const resolvedPage = page || originPage || window.location.pathname;
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          ...getAttribution(),
          page: resolvedPage,
          turnstile_token: token,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Something went wrong. Please email contact@eagentix.com.');
      }
      setState('done');

      // GA4's recommended conversion event. Without this, Google Ads and GA4
      // cannot attribute any lead — including the gclid/fbclid we capture above.
      trackEvent(EVENTS.GENERATE_LEAD, {
        page: resolvedPage,
        form_title: title,
        team_size: form.team_size || '(not set)',
        has_phone: Boolean(form.phone),
        marketing_consent: Boolean(form.marketing_consent),
      });
    } catch (err) {
      setError(err.message);
      setState('error');
      trackEvent(EVENTS.LEAD_SUBMIT_FAILED, { page: page || '', reason: err.message });
      if (window.turnstile && widgetRef.current) {
        try { window.turnstile.reset(); } catch (_) {}
        setToken('');
      }
    }
  };

  if (state === 'done') {
    return (
      <div style={{ background: 'white', borderRadius: '20px', padding: '40px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>✅</div>
        <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', margin: '0 0 12px' }}>Thanks. Your request is in.</h3>
        <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.6 }}>
          {successText}
        </p>
        {successActionUrl && (
          <a
            href={successActionUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block', marginTop: '18px', padding: '14px 24px',
              borderRadius: '12px', background: accent, color: 'white',
              fontWeight: 800, textDecoration: 'none'
            }}
          >
            {successActionLabel}
          </a>
        )}
      </div>
    );
  }

  const input = {
    width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid #cbd5e1',
    fontSize: '1rem', marginBottom: '16px', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const label = { display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a', fontSize: '0.95rem' };

  return (
    <form onSubmit={submit} onFocusCapture={onFirstInteraction} style={{ background: 'white', borderRadius: '20px', padding: '40px', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', maxWidth: '520px', margin: '0 auto', textAlign: 'left' }}>
      <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>{title}</h3>
      <p style={{ color: '#64748b', margin: '0 0 24px', fontSize: '1rem' }}>{subtitle}</p>

      <label htmlFor={`${fieldPrefix}-lead-name`} style={label}>Full name</label>
      <input id={`${fieldPrefix}-lead-name`} name="name" autoComplete="name" style={input} value={form.name} onChange={upd('name')} required placeholder="Your name" />

      <label htmlFor={`${fieldPrefix}-lead-email`} style={label}>Work email</label>
      <input id={`${fieldPrefix}-lead-email`} name="work_email" autoComplete="email" style={input} type="email" value={form.work_email} onChange={upd('work_email')} required placeholder="you@company.com" />

      <label htmlFor={`${fieldPrefix}-lead-company`} style={label}>Company</label>
      <input id={`${fieldPrefix}-lead-company`} name="company" autoComplete="organization" style={input} value={form.company} onChange={upd('company')} required placeholder="Company name" />

      {showJobTitle && (
        <>
          <label htmlFor={`${fieldPrefix}-lead-title`} style={label}>{jobTitleLabel}</label>
          <input id={`${fieldPrefix}-lead-title`} name="job_title" autoComplete="organization-title" style={input} value={form.job_title} onChange={upd('job_title')} placeholder="e.g. COO, Head of Operations" />
        </>
      )}

      {showPhone && (
        <>
          <label htmlFor={`${fieldPrefix}-lead-phone`} style={label}>Phone (optional)</label>
          <input id={`${fieldPrefix}-lead-phone`} name="phone" autoComplete="tel" style={input} type="tel" value={form.phone} onChange={upd('phone')} placeholder="+65 9123 4567" />
        </>
      )}

      {showTeamSize && (
        <>
          <label htmlFor={`${fieldPrefix}-lead-team-size`} style={label}>{teamSizeLabel}</label>
          <select id={`${fieldPrefix}-lead-team-size`} name="team_size" style={input} value={form.team_size} onChange={upd('team_size')}>
            <option value="">Select...</option>
            {teamSizeOptions.map((option) => <option key={option}>{option}</option>)}
          </select>
        </>
      )}

      <label htmlFor={`${fieldPrefix}-lead-message`} style={label}>{messageLabel}</label>
      <textarea id={`${fieldPrefix}-lead-message`} name="message" style={{ ...input, minHeight: '90px', resize: 'vertical' }} value={form.message} onChange={upd('message')} placeholder={messagePlaceholder} />

      <label htmlFor={`${fieldPrefix}-lead-consent`} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '4px 0 18px', color: '#475569', fontSize: '0.9rem', lineHeight: 1.5, cursor: 'pointer' }}>
        <input
          id={`${fieldPrefix}-lead-consent`}
          name="marketing_consent"
          type="checkbox"
          checked={form.marketing_consent}
          onChange={(e) => setForm((f) => ({ ...f, marketing_consent: e.target.checked }))}
          style={{ width: '18px', height: '18px', flex: '0 0 18px', marginTop: '1px', accentColor: accent, cursor: 'pointer' }}
        />
        <span>{consentLabel}</span>
      </label>

      {/* honeypot — hidden from humans */}
      <input name="website" value={form.website} onChange={upd('website')} tabIndex="-1" autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} aria-hidden="true" />

      {SITE_KEY && <div ref={widgetRef} style={{ margin: '8px 0 16px' }} />}

      {error && <div style={{ color: '#e11d48', fontSize: '0.9rem', marginBottom: '12px' }}>{error}</div>}

      <button type="submit" disabled={state === 'sending'}
        style={{ width: '100%', background: accent, color: 'white', padding: '16px', borderRadius: '12px',
          fontWeight: 700, fontSize: '1.1rem', border: 'none', cursor: 'pointer',
          boxShadow: '0 10px 25px rgba(0,113,227,0.35)', opacity: state === 'sending' ? 0.7 : 1 }}>
        {state === 'sending' ? 'Sending…' : buttonLabel}
      </button>
    </form>
  );
}
